// src/components/dashboard/DashboardShopPurchases.js
// eslint-disable-next-line no-unused-vars
import React, {useState, useEffect} from 'react';
import styled from 'styled-components';
import {useTranslation} from 'react-i18next';
import {getPurchaseShopItems, getActiveServers} from '../../../api/axios';
import {useAuth} from '../../../hooks/useAuth';
import {Navigate} from 'react-router-dom';

const DashboardShopPurchases = () => {
  const {t} = useTranslation();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const {user} = useAuth();

  // Filter States:
  const [paymentFilter, setPaymentFilter] = useState('All'); // "All", "Arcanium", "Vote Points"
  const [searchTerm, setSearchTerm] = useState('');

  // NEU: Server-Filter: "All", "hybrid", "pvp", "saison"
  const [serverFilter, setServerFilter] = useState();

  // State für mobile Ansicht
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [activeServers, setActiveServers] = useState([]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      getPurchaseShopItems()
        .then(response => {
          let data = response.data;
          // Falls die API-Daten kein Array liefern, in ein Array umwandeln:
          if (!Array.isArray(data)) {
            data = Object.values(data);
          }
          setLogs(data);
          setLoading(false);
        })
        .catch(error => {
          console.error('Error fetching shop purchases:', error);
          setLoading(false);
        });
    }, 1000); // 1000ms Delay

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    getActiveServers()
      .then(res => {
        const list = Array.isArray(res?.data?.servers) ? res.data.servers : [];
        setActiveServers(list);

        // Wenn genau ein aktiver Server → direkt als Filter setzen
        if (list.length === 1 && list[0]?.friendly_key) {
          setServerFilter(list[0].friendly_key);
        }

      })
      .catch(() => setActiveServers([]));
  }, []);

  // Filtere Logs nach Payment Type und Suchbegriff (Item Name oder Order-ID)
  const filteredLogs = logs.filter(log => {
    const matchesPayment =
      paymentFilter === 'All' || (log.type && log.type.toLowerCase() === paymentFilter.toLowerCase());

    const matchesSearch =
      searchTerm.trim() === '' || (log.item_name && log.item_name.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesServer =
      !serverFilter || // wenn leer → kein Serverfilter
      (log.server_key && log.server_key.toLowerCase() === serverFilter.toLowerCase());

    return matchesPayment && matchesSearch && matchesServer;
  });

  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);
  const currentLogs = filteredLogs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Wenn Filter oder Suche ändern, zurück zu Seite 1
  useEffect(() => {
    setCurrentPage(1);
  }, [paymentFilter, searchTerm, serverFilter]);

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return (
    <Container>
      {loading ? (
        <p>{t('dashboardShop.loadingPurchases')}</p>
      ) : (
        <>
          <Summary>
            <SummaryText>{t('dashboardShop.totalPurchases', {count: filteredLogs.length})}</SummaryText>
          </Summary>
          <FilterLabel>{t('dashboardShop.paymentType')}</FilterLabel>
          <FilterContainer>
            <FilterButton active={paymentFilter === 'All'} onClick={() => setPaymentFilter('All')}>
              {t('dashboardShop.filter.all')}
            </FilterButton>
            <FilterButton active={paymentFilter === 'Arcanium'} onClick={() => setPaymentFilter('Arcanium')}>
              {t('dashboardShop.filter.arcanium')}
            </FilterButton>
            <FilterButton active={paymentFilter === 'Vote Points'} onClick={() => setPaymentFilter('Vote Points')}>
              {t('dashboardShop.filter.votePoints')}
            </FilterButton>
          </FilterContainer>

          {activeServers.length > 1 && (
            <>
              <FilterLabel>Server</FilterLabel>
              <FilterContainer>
                  <ServerSelect value={serverFilter} onChange={e => setServerFilter(e.target.value)}>
                    <option value="">All</option>
                    {activeServers.map(s => (
                      <option key={s.friendly_key} value={s.friendly_key}>
                        {s.display_name || s.friendly_key}
                      </option>
                    ))}
                  </ServerSelect>
              </FilterContainer>
            </>
          )}
          <SearchInput
            type="text"
            placeholder={t('dashboardShop.searchPlaceholder')}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            autoComplete="OFF"
          />
          <PaginationContainer>
            <PageButton onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage <= 1}>
              {t('dashboardShop.prev')}
            </PageButton>
            <PageIndicator>
              {filteredLogs.length > 0 ? `${currentPage}/${totalPages}` : t('dashboardShop.pageIndicatorZero')}
            </PageIndicator>
            <PageButton
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage >= totalPages || totalPages === 0}
            >
              {t('dashboardShop.next')}
            </PageButton>
          </PaginationContainer>
          {isMobile ? (
            <CardsGrid>
              {currentLogs.map((log, index) => (
                <Card key={`${log.id}-${index}`}>
                  <CardIcon>
                    <img
                      src={`/assets/items/${log.item_icon ? log.item_icon + '.jpg' : 'unknown.jpg'}`}
                      alt={log.item_name}
                    />
                  </CardIcon>
                  <CardContent>
                    <CardTitle>{log.item_name}</CardTitle>
                    <CardRow>
                      <span>
                        <strong>{t('dashboardShop.card.count')}:</strong> {log.item_count}
                      </span>
                    </CardRow>
                    <CardRow>
                      <span>
                        <strong>{t('dashboardShop.card.date')}:</strong> {new Date(log.buy_date).toLocaleString()}
                      </span>
                    </CardRow>
                    <CardRow>
                      <span>
                        <strong>{t('dashboardShop.card.type')}:</strong> {log.type}
                      </span>
                    </CardRow>
                    <CardRow>
                      <span>
                        <strong>Server:</strong> {log.server_key}
                      </span>
                    </CardRow>
                  </CardContent>
                </Card>
              ))}
            </CardsGrid>
          ) : (
            <StyledTable>
              <thead>
                <tr>
                  <Th></Th>
                  <Th>{t('dashboardShop.th.itemName')}</Th>
                  <Th>{t('dashboardShop.th.count')}</Th>
                  <Th>{t('dashboardShop.th.date')}</Th>
                  <Th>{t('dashboardShop.th.paymentType')}</Th>
                  <Th>{'Server'}</Th>
                </tr>
              </thead>
              <tbody>
                {currentLogs.map((log, index) => (
                  <tr key={`${log.id}-${index}`}>
                    <Td>
                      <Icon
                        src={`/assets/items/${log.item_icon ? log.item_icon + '.jpg' : 'unknown.jpg'}`}
                        alt={log.item_name}
                      />
                    </Td>
                    <Td>{log.item_name}</Td>
                    <Td>{log.item_count}</Td>
                    <Td>{new Date(log.buy_date).toLocaleString()}</Td>
                    <Td>{log.type}</Td>
                    <Td>{log.server_key.charAt(0).toUpperCase() + log.server_key.slice(1)}</Td>
                  </tr>
                ))}
              </tbody>
            </StyledTable>
          )}
          <PaginationContainer>
            <PageButton onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage <= 1}>
              {t('dashboardShop.prev')}
            </PageButton>
            <PageIndicator>
              {filteredLogs.length > 0 ? `${currentPage}/${totalPages}` : t('dashboardShop.pageIndicatorZero')}
            </PageIndicator>
            <PageButton
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage >= totalPages || totalPages === 0}
            >
              {t('dashboardShop.next')}
            </PageButton>
          </PaginationContainer>
        </>
      )}
    </Container>
  );
};

export default DashboardShopPurchases;

//
// ===== Styled Components =====
const Container = styled.div`
  padding: 20px;
  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const Summary = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 10px;
  font-size: 1rem;
  @media (max-width: 768px) {
    justify-content: center;
    font-size: 0.9rem;
  }
`;

const SummaryText = styled.span`
  font-weight: bold;
`;

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  flex-wrap: wrap;
  justify-content: center;
  @media (max-width: 768px) {
    align-items: stretch;
  }
`;

const FilterLabel = styled.span`
  font-weight: bold;
`;

const FilterButton = styled.button`
  padding: 6px 12px;
  border: none;
  color: ${({active}) => (active ? '#000' : '#fff')};
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.3s ease;
  @media (max-width: 768px) {
    width: 120px;
  }
`;

const ServerSelect = styled.select`
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid #ccc;
  min-width: 180px;
  height: 36px;
  background: #000;
  color: #fff;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchInput = styled.input`
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid #ccc;
  flex: 1;
  height: 50px;
  min-width: 200px;
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const PaginationContainer = styled.div`
  margin-top: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  @media (max-width: 768px) {
    gap: 5px;
  }
`;

const PageButton = styled.button`
  padding: 6px 12px;
  border: none;
  color: #fff;
  cursor: pointer;
  border-radius: 4px;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  @media (max-width: 768px) {
    width: 120px;
  }
`;

const PageIndicator = styled.span`
  font-weight: bold;
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

/* Table Layout für Desktop */
const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 1rem;
  @media (max-width: 768px) {
    display: none;
  }
`;

const Th = styled.th`
  border-bottom: 2px solid #ddd;
  padding: 10px;
  @media (max-width: 768px) {
    padding: 8px;
  }
`;

const Td = styled.td`
  border-bottom: 1px solid #ddd;
  padding: 10px;
  @media (max-width: 768px) {
    padding: 8px;
  }
`;

const Icon = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
`;

/* Card Layout für Mobile */
const CardsGrid = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: grid;

    gap: 15px;
    margin-bottom: 20px;
  }
`;

const Card = styled.div`
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 15px;
  display: flex;
  gap: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const CardIcon = styled.div`
  flex-shrink: 0;
  img {
    width: 50px;
    height: 50px;
    object-fit: contain;
  }
`;

const CardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 0.95rem;
  @media (max-width: 768px) {
    text-align: center;
    font-size: 0.9rem;
  }
`;

const CardTitle = styled.h4`
  margin: 0;
  font-size: 1.1rem;
`;

const CardRow = styled.div`
  span {
    display: block;
  }
`;
